import { useState, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { trackLeadCreated } from "@/lib/analytics";
import { buildTrackingFields, recordLeadInDb } from "@/lib/lead";
import { markFormFocus, parsePhoneBR } from "@/lib/tracking";
import { checkAntispam, markAntispamSubmitted, antispamMessagePt } from "@/lib/antispam";
import { useLocale } from "@/i18n/LocaleProvider";

const CAREERS_WEBHOOK_URL =
  "https://kentrosistemas.atenderbem.com/webhookcapture/capture/f220cf1eb0c84d28bf4c806b59d1f031";

const AREAS = ["Vendas", "Operação", "Administrativo", "Desenvolvimento"] as const;

const CONSENT_TEXT =
  "Aceito ceder meus dados para Kentro e parceiros com finalidade de contratação e entrevistas de emprego";

type CareersFormValues = {
  name: string;
  phone: string;
  email: string;
  area: (typeof AREAS)[number];
  linkedin?: string;
  instagram?: string;
  resumeUrl: string;
  consent: boolean;
};

export function CareersForm() {
  const locale = useLocale();
  const [done, setDone] = useState(false);
  const formOpenedAtRef = useRef<number>(Date.now());
  const honeypotRef = useRef<HTMLInputElement>(null);

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(2, "Informe seu nome").max(120),
        email: z.string().trim().email("E-mail inválido").max(200),
        phone: z
          .string()
          .trim()
          .min(8, "Telefone inválido")
          .max(25)
          .regex(/^[\d\s()+\-.]+$/, "Use apenas números")
          .refine(
            (v) => {
              const digits = v.replace(/\D/g, "");
              return digits.length >= 6 && digits.length <= 15;
            },
            { message: "Informe um telefone válido (com DDD ou código do país)" },
          ),
        area: z.enum(AREAS, { errorMap: () => ({ message: "Selecione uma área" }) }),
        linkedin: z.string().trim().max(300).optional().or(z.literal("")),
        instagram: z.string().trim().max(300).optional().or(z.literal("")),
        resumeUrl: z
          .string()
          .trim()
          .min(8, "Informe o link do currículo")
          .max(500)
          .refine((v) => /^https?:\/\/\S+\.\S+/.test(v), {
            message: "Use um link acessível começando com https://",
          }),
        consent: z.boolean().refine((v) => v === true, {
          message: "É necessário aceitar para enviar",
        }),
      }),
    [],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CareersFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      area: "Vendas",
      linkedin: "",
      instagram: "",
      resumeUrl: "",
      consent: false,
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    const scope = "trabalhe-conosco";
    const spam = await checkAntispam({
      scope,
      honeypot: honeypotRef.current?.value ?? "",
      formOpenedAt: formOpenedAtRef.current,
      contentKey: `${data.name}|${data.email}|${data.phone}|${data.resumeUrl}`,
    });
    if (!spam.ok) {
      toast.error(antispamMessagePt(spam) ?? "Não foi possível enviar agora.");
      return;
    }

    try {
      const formId = "kentro-trabalhe-conosco";
      let trackingFields: Record<string, string> = {};
      try {
        trackingFields = buildTrackingFields(formId, "trabalhe-conosco") || {};
      } catch (e) {
        console.warn("[trabalhe-conosco] buildTrackingFields falhou", e);
      }

      const phone = parsePhoneBR(data.phone);
      const s = (v: unknown) => (typeof v === "string" ? v.trim() : v == null ? "" : String(v));

      const fields: Record<string, string> = {
        name: s(data.name),
        email: s(data.email),
        phone: phone.national,
        phone_e164: phone.e164,
        phone_ddd: phone.ddd,
        phone_national: phone.national,
        phone_valid: String(phone.valid),
        lead_type: "candidato",
        interest_area: s(data.area),
        linkedin: s(data.linkedin),
        instagram: s(data.instagram),
        resume_url: s(data.resumeUrl),
        consent_recruitment: "true",
        consent_text: CONSENT_TEXT,
        locale: s(locale),
      };
      for (const [k, v] of Object.entries(trackingFields)) {
        fields[k] = s(v);
      }

      const payload = {
        fields,
        page_url: typeof window !== "undefined" ? window.location.href : "",
        pathname: typeof window !== "undefined" ? window.location.pathname : "",
        language: typeof navigator !== "undefined" ? navigator.language : "",
        timestamp: new Date().toISOString(),
        form_id: formId,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      };

      const res = await fetch(CAREERS_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Webhook respondeu ${res.status}`);

      recordLeadInDb({ ...fields, page_url: payload.page_url });
      trackLeadCreated("trabalhe-conosco");
      markAntispamSubmitted(scope, spam.hash);
      setDone(true);
      reset();
      formOpenedAtRef.current = Date.now();
      toast.success("Candidatura enviada!", {
        description: "Nosso time de pessoas vai analisar seu perfil.",
      });
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível enviar", { description: "Tente novamente em instantes." });
    }
  });

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-2xl p-8 text-center"
      >
        <CheckCircle2 className="mx-auto mb-4 size-12 text-primary-glow" />
        <h3 className="text-xl font-semibold">Recebemos sua candidatura</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Se seu perfil combinar com uma vaga aberta, nosso time entra em contato pelos dados
          informados.
        </p>
        <Button variant="ghost" className="mt-4" onClick={() => setDone(false)}>
          Enviar outra
        </Button>
      </motion.div>
    );
  }

  const inputCls =
    "w-full rounded-lg bg-secondary/60 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-primary-glow focus:ring-2 focus:ring-primary/30";

  return (
    <form
      onSubmit={onSubmit}
      onFocus={() => markFormFocus("kentro-trabalhe-conosco")}
      className="space-y-4"
    >
      {/* honeypot anti-bot */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="hp-carreiras">Do not fill</label>
        <input
          ref={honeypotRef}
          id="hp-carreiras"
          name="hp_url"
          type="text"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Nome *</label>
          <input className={inputCls} placeholder="Seu nome completo" {...register("name")} />
          {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Telefone / WhatsApp *
          </label>
          <input
            className={inputCls}
            placeholder="(38) 91234-5678"
            inputMode="tel"
            {...register("phone")}
          />
          {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone.message}</p>}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">E-mail *</label>
          <input className={inputCls} type="email" placeholder="voce@email.com" {...register("email")} />
          {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Área de interesse *
          </label>
          <select aria-label="Área de interesse" className={inputCls} {...register("area")}>
            {AREAS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          {errors.area && <p className="mt-1 text-xs text-destructive">{errors.area.message}</p>}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">LinkedIn</label>
          <input
            className={inputCls}
            placeholder="https://linkedin.com/in/seu-perfil"
            {...register("linkedin")}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Instagram</label>
          <input className={inputCls} placeholder="@seuperfil" {...register("instagram")} />
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
          Link do currículo (acessível) *
        </label>
        <input
          className={inputCls}
          placeholder="https://drive.google.com/... (deixe o link público)"
          {...register("resumeUrl")}
        />
        {errors.resumeUrl && (
          <p className="mt-1 text-xs text-destructive">{errors.resumeUrl.message}</p>
        )}
      </div>

      <label className="flex items-start gap-3 rounded-lg border border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
        <input
          type="checkbox"
          className="mt-0.5 size-4 shrink-0 accent-primary"
          {...register("consent")}
        />
        <span>{CONSENT_TEXT}.</span>
      </label>
      {errors.consent && <p className="text-xs text-destructive">{errors.consent.message}</p>}

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full gradient-primary text-white shadow-glow hover:opacity-95"
        size="lg"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="animate-spin" /> Enviando...
          </>
        ) : (
          <>
            <Send /> Enviar minha candidatura
          </>
        )}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Seus dados são tratados conforme nossa{" "}
        <a href="/privacidade" className="underline hover:text-foreground">
          Política de Privacidade
        </a>
        .
      </p>
    </form>
  );
}
